import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUp,
} from "react-feather";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import { fetchWardsThunk } from "../../../store/slices/wardsSlice";
import { fetchMeterTypesThunk } from "../../../store/slices/meterTypesSlice";
import { fetchSlrmTypesThunk } from "../../../store/slices/slrmTypesSlice";

import {
  fetchHouseListReportThunk,
  clearHouseListReport,
} from "../../../store/slices/houseListReportSlice";

import {
  KANNADA_FONT_BASE64,
  KANNADA_FONT_NAME,
} from "../../../utils/kannadaFontBase64";

import "./ReportPage.css";

const HouseListReportPage = () => {
  const dispatch = useDispatch();

  const wards = useSelector(
    (state) => state.wards?.list || [],
  );

  const meterTypes = useSelector(
    (state) => state.meterTypes?.list || [],
  );

  const slrmTypes = useSelector(
    (state) => state.slrmTypes?.list || [],
  );

  const {
    rows = [],
    meta = {},
    loading = false,
    error = null,
  } = useSelector(
    (state) => state.houseListReport || {},
  );

  const [wardId, setWardId] = useState("");
  const [mtrType, setMtrType] = useState("");
  const [slrmType, setSlrmType] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState(20);

  const [sortConfig, setSortConfig] =
    useState({
      key: "home_id",
      direction: "desc",
    });

  const [exportLoading, setExportLoading] =
    useState(false);

  // =====================================================
  // REPORT COLUMNS
  // SAME FOR UI + EXCEL + PDF
  // =====================================================

  const reportColumns = [
    {
      key: "house_serial",
      sortKey: "home_id",
      label: "Sl No.",
      width: 80,
      excelWidth: 8,
      pdfWidth: "5%",
    },
    {
      key: "qr_code",
      sortKey: "qr_code",
      label: "QR Code",
      excelWidth: 14,
      pdfWidth: "8%",
    },
    {
      key: "door_no",
      sortKey: "door_no",
      label: "Door No",
      excelWidth: 16,
      pdfWidth: "8%",
    },
    {
      key: "family_head",
      sortKey: "family_head",
      label: "Family Head",
      excelWidth: 42,
      pdfWidth: "22%",
    },
    {
      key: "ward_name",
      sortKey: "ward_name",
      label: "Ward",
      excelWidth: 20,
      pdfWidth: "8%",
    },
    {
      key: "mobile",
      sortKey: "mobile",
      label: "Mobile",
      excelWidth: 16,
      pdfWidth: "9%",
    },
    {
      key: "water_balance",
      sortKey: "water_balance",
      label: "Water Balance",
      excelWidth: 16,
      pdfWidth: "8%",
    },
    {
      key: "slrm_bal",
      sortKey: "slrm_bal",
      label: "SLRM Balance",
      excelWidth: 16,
      pdfWidth: "8%",
    },
    {
      key: "balance",
      sortKey: "balance",
      label: "Total Balance",
      excelWidth: 16,
      pdfWidth: "8%",
    },
    {
      key: "water_type_name",
      sortKey: "water_type_name",
      label: "Water Rate",
      excelWidth: 18,
      pdfWidth: "6%",
    },
    {
      key: "slrm_type_name",
      sortKey: "slrm_type_name",
      label: "SLRM Rate",
      excelWidth: 18,
      pdfWidth: "6%",
    },
    {
      key: "status",
      sortKey: "status",
      label: "Status",
      excelWidth: 14,
      pdfWidth: "4%",
    },
  ];

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    dispatch(fetchWardsThunk());
    dispatch(fetchMeterTypesThunk());
    dispatch(fetchSlrmTypesThunk());
  }, [dispatch]);

  // =====================================================
  // REQUEST FILTERS
  // =====================================================

  const getRequestFilters = (
    requestPage = page,
    requestPageSize = pageSize,
    exportValue = 0,
  ) => ({
    ward_id: wardId,
    mtr_type: mtrType,
    slrm_type: slrmType,
    search,
    page: requestPage,
    pageSize: requestPageSize,
    sort_field: sortConfig.key,
    sort_direction: sortConfig.direction,
    export: exportValue,
  });

  const loadReport = (
    requestPage = page,
    requestPageSize = pageSize,
    exportValue = 0,
  ) =>
    dispatch(
      fetchHouseListReportThunk(
        getRequestFilters(
          requestPage,
          requestPageSize,
          exportValue,
        ),
      ),
    );

  // =====================================================
  // AUTO LOAD
  // =====================================================

  useEffect(() => {
    if (!pageSize) {
      return;
    }

    loadReport(page, pageSize, 0);
  }, [
    wardId,
    mtrType,
    slrmType,
    search,
    page,
    pageSize,
    sortConfig.key,
    sortConfig.direction,
  ]);

  // =====================================================
  // LOAD
  // =====================================================

  const handleLoad = () => {
    setPage(1);
    loadReport(1, pageSize, 0);
  };

  // =====================================================
  // CLEAR
  // =====================================================

  const handleClear = () => {
    setWardId("");
    setMtrType("");
    setSlrmType("");
    setSearch("");

    setPage(1);
    setPageSize(20);

    setSortConfig({
      key: "home_id",
      direction: "desc",
    });

    dispatch(clearHouseListReport());
  };

  // =====================================================
  // SORT
  // =====================================================

  const handleSort = (key) => {
    setPage(1);

    setSortConfig((previous) => ({
      key,
      direction:
        previous.key === key &&
        previous.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  // =====================================================
  // SAFE VALUE
  // =====================================================

  const safeText = (value) => {
    if (
      value === null ||
      value === undefined
    ) {
      return "";
    }

    return String(value);
  };

  // =====================================================
  // EXCEL EXPORT
  // =====================================================

  const exportExcel = async () => {
    if (exportLoading) {
      return;
    }

    try {
      setExportLoading(true);

      const response = await loadReport(
        1,
        999999,
        1,
      ).unwrap();

      const exportRows =
        response?.data || [];

      const data = exportRows.map(
        (row) => {
          const exportRow = {};

          reportColumns.forEach(
            (column) => {
              exportRow[column.label] =
                row[column.key] ?? "";
            },
          );

          return exportRow;
        },
      );

      const worksheet =
        XLSX.utils.json_to_sheet(data);

      worksheet["!cols"] =
        reportColumns.map(
          (column) => ({
            wch:
              column.excelWidth ||
              15,
          }),
        );

      const workbook =
        XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "House List",
      );

      XLSX.writeFile(
        workbook,
        "house_list_report.xlsx",
      );
    } catch (exportError) {
      console.error(
        "Excel export failed:",
        exportError,
      );
    } finally {
      setExportLoading(false);
    }
  };

  // =====================================================
  // CREATE KANNADA FONT CSS
  // =====================================================

  const createKannadaFontStyle = () => {
    const style =
      document.createElement("style");

    style.id =
      "house-list-kannada-pdf-font";

    style.innerHTML = `
      @font-face {
        font-family: '${KANNADA_FONT_NAME}';
        src: url(data:font/ttf;base64,${KANNADA_FONT_BASE64})
          format('truetype');
        font-weight: normal;
        font-style: normal;
      }
    `;

    document.head.appendChild(style);

    return style;
  };

  // =====================================================
  // CREATE ONE PDF HTML PAGE
  // =====================================================

  const createPdfPageElement = (
    pageRows,
    pageNumber,
    totalPdfPages,
    totalRecords,
  ) => {
    const wrapper =
      document.createElement("div");

    wrapper.style.position =
      "absolute";

    wrapper.style.left =
      "-100000px";

    wrapper.style.top = "0";

    wrapper.style.width =
      "1120px";

    wrapper.style.background =
      "#ffffff";

    wrapper.style.padding =
      "20px";

    wrapper.style.boxSizing =
      "border-box";

    wrapper.style.fontFamily =
      `'${KANNADA_FONT_NAME}', 'Noto Sans Kannada', Arial, sans-serif`;

    wrapper.style.color =
      "#000000";

    // =================================================
    // TITLE
    // =================================================

    const title =
      document.createElement("div");

    title.textContent =
      "House List - Report";

    title.style.fontSize =
      "20px";

    title.style.fontWeight =
      "600";

    title.style.marginBottom =
      "6px";

    wrapper.appendChild(title);

    // =================================================
    // META
    // =================================================

    const metaRow =
      document.createElement("div");

    metaRow.style.display =
      "flex";

    metaRow.style.justifyContent =
      "space-between";

    metaRow.style.alignItems =
      "center";

    metaRow.style.fontSize =
      "12px";

    metaRow.style.marginBottom =
      "12px";

    const totalText =
      document.createElement("span");

    totalText.textContent =
      `Total Records: ${totalRecords}`;

    const pageText =
      document.createElement("span");

    pageText.textContent =
      `Page ${pageNumber} / ${totalPdfPages}`;

    metaRow.appendChild(
      totalText,
    );

    metaRow.appendChild(
      pageText,
    );

    wrapper.appendChild(
      metaRow,
    );

    // =================================================
    // TABLE
    // =================================================

    const table =
      document.createElement("table");

    table.style.width = "100%";

    table.style.borderCollapse =
      "collapse";

    table.style.tableLayout =
      "fixed";

    table.style.fontSize =
      "10px";

    // =================================================
    // HEADER
    // =================================================

    const thead =
      document.createElement("thead");

    const headerRow =
      document.createElement("tr");

    reportColumns.forEach(
      (column) => {
        const th =
          document.createElement("th");

        th.textContent =
          column.label;

        th.style.border =
          "1px solid #9ca3af";

        th.style.padding =
          "6px 4px";

        th.style.textAlign =
          "center";

        th.style.fontWeight =
          "600";

        th.style.background =
          "#f3f4f6";

        th.style.verticalAlign =
          "middle";

        th.style.wordBreak =
          "break-word";

        th.style.width =
          column.pdfWidth ||
          "auto";

        headerRow.appendChild(
          th,
        );
      },
    );

    thead.appendChild(
      headerRow,
    );

    table.appendChild(
      thead,
    );

    // =================================================
    // BODY
    // =================================================

    const tbody =
      document.createElement("tbody");

    pageRows.forEach((row) => {
      const tr =
        document.createElement("tr");

      reportColumns.forEach(
        (column) => {
          const td =
            document.createElement("td");

          td.textContent =
            safeText(
              row[column.key],
            );

          td.style.border =
            "1px solid #d1d5db";

          td.style.padding =
            "5px 4px";

          td.style.verticalAlign =
            "middle";

          td.style.wordBreak =
            "break-word";

          td.style.overflowWrap =
            "anywhere";

          td.style.lineHeight =
            "1.35";

          // Numeric balances
          if (
            [
              "water_balance",
              "slrm_bal",
              "balance",
            ].includes(
              column.key,
            )
          ) {
            td.style.textAlign =
              "right";
          } else if (
            [
              "house_serial",
              "status",
            ].includes(
              column.key,
            )
          ) {
            td.style.textAlign =
              "center";
          } else {
            td.style.textAlign =
              "left";
          }

          tr.appendChild(td);
        },
      );

      tbody.appendChild(tr);
    });

    table.appendChild(tbody);

    wrapper.appendChild(table);

    document.body.appendChild(
      wrapper,
    );

    return wrapper;
  };

  // =====================================================
  // PDF EXPORT
  // KANNADA-SAFE VERSION
  // Browser shapes Kannada first
  // html2canvas captures it
  // jsPDF only places image
  // =====================================================

const exportPDF = async () => {
  if (exportLoading) {
    return;
  }

  let fontStyle = null;

  try {
    setExportLoading(true);

    // =====================================================
    // LOAD ALL ROWS
    // =====================================================

    const response = await loadReport(
      1,
      999999,
      1,
    ).unwrap();

    const exportRows =
      response?.data || [];

    if (!exportRows.length) {
      return;
    }

    const totalRecords =
      response?.meta?.totalRecords ??
      exportRows.length;

    // =====================================================
    // ADD KANNADA FONT
    // =====================================================

    const existingStyle =
      document.getElementById(
        "house-list-kannada-pdf-font",
      );

    if (!existingStyle) {
      fontStyle =
        createKannadaFontStyle();
    }

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      await document.fonts.ready;
    }

    // =====================================================
    // PDF CONFIG
    // A4 LANDSCAPE
    // =====================================================

    const pdf = new jsPDF({
      orientation:
        "landscape",

      unit:
        "mm",

      format:
        "a4",

      compress:
        true,
    });

    const pdfWidth =
      pdf.internal.pageSize.getWidth();

    const pdfHeight =
      pdf.internal.pageSize.getHeight();

    const margin = 7;

    const usableWidth =
      pdfWidth -
      margin * 2;

    const usableHeight =
      pdfHeight -
      margin * 2;

    // =====================================================
    // ROWS PER PDF PAGE
    // Keep smaller because Kannada names can wrap
    // =====================================================

    const rowsPerPage = 18;

    const totalPdfPages =
      Math.ceil(
        exportRows.length /
          rowsPerPage,
      );

    // =====================================================
    // GENERATE EACH PAGE
    // =====================================================

    for (
      let pageIndex = 0;
      pageIndex <
      totalPdfPages;
      pageIndex += 1
    ) {
      const startIndex =
        pageIndex *
        rowsPerPage;

      const endIndex =
        startIndex +
        rowsPerPage;

      const pageRows =
        exportRows.slice(
          startIndex,
          endIndex,
        );

      // ===================================================
      // CREATE HIDDEN HTML PAGE
      // ===================================================

      const pageElement =
        createPdfPageElement(
          pageRows,
          pageIndex + 1,
          totalPdfPages,
          totalRecords,
        );

      // Give browser time to render Kannada
      await new Promise(
        (resolve) =>
          setTimeout(
            resolve,
            100,
          ),
      );

      if (
        document.fonts &&
        document.fonts.ready
      ) {
        await document.fonts.ready;
      }

      // ===================================================
      // HTML -> CANVAS
      // ===================================================

      const canvas =
        await html2canvas(
          pageElement,
          {
            scale: 2,

            useCORS:
              true,

            backgroundColor:
              "#ffffff",

            logging:
              false,

            allowTaint:
              true,

            windowWidth:
              1160,
          },
        );

      // Remove temporary HTML immediately
      pageElement.remove();

      // ===================================================
      // CANVAS -> IMAGE
      // ===================================================

      const imageData =
        canvas.toDataURL(
          "image/jpeg",
          0.92,
        );

      const imageWidth =
        canvas.width;

      const imageHeight =
        canvas.height;

      const imageRatio =
        imageHeight /
        imageWidth;

      let renderWidth =
        usableWidth;

      let renderHeight =
        renderWidth *
        imageRatio;

      // Make sure image does not exceed page height
      if (
        renderHeight >
        usableHeight
      ) {
        renderHeight =
          usableHeight;

        renderWidth =
          renderHeight /
          imageRatio;
      }

      const x =
        (
          pdfWidth -
          renderWidth
        ) /
        2;

      const y =
        margin;

      // ===================================================
      // ADD NEW PDF PAGE
      // ===================================================

      if (
        pageIndex > 0
      ) {
        pdf.addPage();
      }

      pdf.addImage(
        imageData,
        "JPEG",
        x,
        y,
        renderWidth,
        renderHeight,
        undefined,
        "FAST",
      );
    }

    // =====================================================
    // DIRECT DOWNLOAD
    // No print window
    // No Save as PDF dialog
    // =====================================================

    pdf.save(
      "house_list_report.pdf",
    );
  } catch (exportError) {
    console.error(
      "PDF export failed:",
      exportError,
    );
  } finally {
    // =====================================================
    // CLEANUP
    // =====================================================

    if (
      fontStyle &&
      fontStyle.parentNode
    ) {
      fontStyle.parentNode.removeChild(
        fontStyle,
      );
    }

    setExportLoading(false);
  }
};

  // =====================================================
  // PAGINATION VALUES
  // =====================================================

  const totalRecords = Number(
    meta?.totalRecords || 0,
  );

  const totalPages = Math.max(
    1,
    Number(meta?.totalPages || 1),
  );

  const currentPage = Math.max(
    1,
    Number(meta?.page || page),
  );

  // =====================================================
  // PAGINATION
  // =====================================================

  const renderPagination = () => {
    if (totalPages <= 1) {
      return null;
    }

    const items = [];
    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage -
        Math.floor(
          maxButtons / 2,
        ),
    );

    let end = Math.min(
      totalPages,
      start +
        maxButtons -
        1,
    );

    start = Math.max(
      1,
      end -
        maxButtons +
        1,
    );

    items.push(
      <Pagination.Prev
        key="previous"
        disabled={
          currentPage === 1 ||
          loading
        }
        onClick={() =>
          setPage(
            (previous) =>
              Math.max(
                1,
                previous - 1,
              ),
          )
        }
      />,
    );

    if (start > 1) {
      items.push(
        <Pagination.Item
          key={1}
          onClick={() =>
            setPage(1)
          }
          disabled={loading}
        >
          1
        </Pagination.Item>,
      );

      if (start > 2) {
        items.push(
          <Pagination.Ellipsis
            key="start-ellipsis"
            disabled
          />,
        );
      }
    }

    for (
      let pageNumber = start;
      pageNumber <= end;
      pageNumber += 1
    ) {
      items.push(
        <Pagination.Item
          key={pageNumber}
          active={
            pageNumber ===
            currentPage
          }
          onClick={() =>
            setPage(
              pageNumber,
            )
          }
          disabled={loading}
        >
          {pageNumber}
        </Pagination.Item>,
      );
    }

    if (
      end <
      totalPages
    ) {
      if (
        end <
        totalPages - 1
      ) {
        items.push(
          <Pagination.Ellipsis
            key="end-ellipsis"
            disabled
          />,
        );
      }

      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() =>
            setPage(
              totalPages,
            )
          }
          disabled={loading}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage ===
            totalPages ||
          loading
        }
        onClick={() =>
          setPage(
            (previous) =>
              Math.min(
                totalPages,
                previous + 1,
              ),
          )
        }
      />,
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  // =====================================================
  // SORTABLE HEADER
  // =====================================================

  const SortableHeader = ({
    columnKey,
    children,
    style,
  }) => {
    const isActive =
      sortConfig.key ===
      columnKey;

    return (
      <th style={style}>
        <button
          type="button"
          className={`report-sort-btn ${
            isActive
              ? "is-active"
              : ""
          }`}
          onClick={() =>
            handleSort(
              columnKey,
            )
          }
          title={`Sort by ${children}`}
        >
          <span>
            {children}
          </span>

          {isActive ? (
            sortConfig.direction ===
            "asc" ? (
              <ChevronUp
                size={14}
              />
            ) : (
              <ChevronDown
                size={14}
              />
            )
          ) : (
            <ChevronsUp
              size={14}
            />
          )}
        </button>
      </th>
    );
  };

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="report-page">
      <Card className="report-card">

        {/* ================= HEADER ================= */}

        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                House List - Report
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          {/* ================= FILTERS ================= */}

          <div className="report-filter-box">
            <Row className="g-3">

              {/* WARD */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Ward
                </Form.Label>

                <Form.Select
                  value={wardId}
                  onChange={(
                    event,
                  ) => {
                    setWardId(
                      event.target
                        .value,
                    );

                    setPage(1);
                  }}
                  disabled={
                    loading
                  }
                >
                  <option value="">
                    Select Ward
                  </option>

                  {wards.map(
                    (ward) => (
                      <option
                        key={
                          ward.ward_id
                        }
                        value={String(
                          ward.ward_id,
                        )}
                      >
                        {
                          ward.ward_name
                        }
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>

              {/* WATER RATE */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Water Rate
                </Form.Label>

                <Form.Select
                  value={mtrType}
                  onChange={(
                    event,
                  ) => {
                    setMtrType(
                      event.target
                        .value,
                    );

                    setPage(1);
                  }}
                  disabled={
                    loading
                  }
                >
                  <option value="">
                    Select Water Rate
                  </option>

                  {meterTypes.map(
                    (
                      meterType,
                    ) => (
                      <option
                        key={
                          meterType.wbs_id
                        }
                        value={String(
                          meterType.wbs_id,
                        )}
                      >
                        {
                          meterType.mtr_type
                        }
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>

              {/* SLRM RATE */}

              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  SLRM Rate
                </Form.Label>

                <Form.Select
                  value={
                    slrmType
                  }
                  onChange={(
                    event,
                  ) => {
                    setSlrmType(
                      event.target
                        .value,
                    );

                    setPage(1);
                  }}
                  disabled={
                    loading
                  }
                >
                  <option value="">
                    Select SLRM Rate
                  </option>

                  {slrmTypes.map(
                    (
                      slrmRate,
                    ) => (
                      <option
                        key={
                          slrmRate.wbs_id
                        }
                        value={String(
                          slrmRate.wbs_id,
                        )}
                      >
                        {
                          slrmRate.mtr_type
                        }
                      </option>
                    ),
                  )}
                </Form.Select>
              </Col>

              {/* ACTIONS */}

              <Col
                lg={3}
                md={6}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={
                      handleLoad
                    }
                    disabled={
                      loading
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={
                      handleClear
                    }
                    disabled={
                      loading
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />

                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          {/* ================= TOOLBAR ================= */}

          <div className="report-table-toolbar">
            <div className="report-table-title">
              House List{" "}

              <span className="report-count">
                (
                {totalRecords.toLocaleString()}{" "}
                records)
              </span>
            </div>

            <div className="report-table-right">

              {/* SEARCH */}

              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search
                    size={16}
                  />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(
                    event,
                  ) => {
                    setSearch(
                      event.target
                        .value,
                    );

                    setPage(1);
                  }}
                  placeholder="Search Door No / Name / Mobile / Water Rate..."
                  disabled={
                    loading
                  }
                />
              </InputGroup>

              {/* EXCEL */}

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={
                  exportExcel
                }
                disabled={
                  !rows.length ||
                  loading ||
                  exportLoading
                }
              >
                {exportLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading
                  ? "Exporting..."
                  : "Excel"}
              </Button>

              {/* PDF */}

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={
                  exportPDF
                }
                disabled={
                  !rows.length ||
                  loading ||
                  exportLoading
                }
              >
                {exportLoading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                ) : (
                  <Download
                    size={16}
                    className="me-2"
                  />
                )}

                {exportLoading
                  ? "Exporting..."
                  : "PDF"}
              </Button>
            </div>
          </div>

          {/* ================= TABLE ================= */}

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    {reportColumns.map(
                      (
                        column,
                      ) => (
                        <SortableHeader
                          key={
                            column.key
                          }
                          columnKey={
                            column.sortKey
                          }
                          style={
                            column.width
                              ? {
                                  width:
                                    column.width,
                                }
                              : undefined
                          }
                        >
                          {
                            column.label
                          }
                        </SortableHeader>
                      ),
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />

                        Loading...
                      </td>
                    </tr>
                  ) : rows.length ? (
                    rows.map(
                      (
                        row,
                        index,
                      ) => (
                        <tr
                          key={String(
                            row.home_id ||
                              index,
                          )}
                        >
                          {reportColumns.map(
                            (
                              column,
                            ) => (
                              <td
                                key={
                                  column.key
                                }
                              >
                                {row[
                                  column.key
                                ] ??
                                  "-"}
                              </td>
                            ),
                          )}
                        </tr>
                      ),
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          reportColumns.length
                        }
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* ================= PAGINATION ================= */}

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  onChange={(
                    event,
                  ) => {
                    setPage(1);

                    setPageSize(
                      Number(
                        event.target
                          .value,
                      ),
                    );
                  }}
                  className="page-size-select"
                  disabled={
                    loading
                  }
                >
                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (size) => (
                      <option
                        key={
                          size
                        }
                        value={
                          size
                        }
                      >
                        {size}
                      </option>
                    ),
                  )}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page{" "}
                {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default HouseListReportPage;